// Standalone Nav + Footer for v14 comparison variant.
// Mountable on interior pages via veaury — uses shared Link, no Inertia react import.
import React, { useState, useEffect } from 'react';
import { Link } from '../_shared.jsx';

const theme = {
  bg: '#fafbfc', fg: '#0f172a', muted: '#64748b',
  border: '#e4e7eb', accent: '#10b981', accentFg: '#ffffff',
  inputBg: '#ffffff', radius: 8,
  font: '"Inter", system-ui, sans-serif',
  mono: '"IBM Plex Mono", ui-monospace, SFMono-Regular, Menlo, monospace',
  cream: '#fbfaf6', dark: '#0c0e10', subtle: '#f8fafc',
};

const useIsMobile = () => {
  const [isMobile, setIsMobile] = useState(() => typeof window !== 'undefined' && window.matchMedia ? window.matchMedia('(max-width: 768px)').matches : true);
  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return;
    const mq = window.matchMedia('(max-width: 768px)');
    const update = () => setIsMobile(mq.matches);
    update();
    mq.addEventListener ? mq.addEventListener('change', update) : mq.addListener(update);
    return () => { mq.removeEventListener ? mq.removeEventListener('change', update) : mq.removeListener(update); };
  }, []);
  return isMobile;
};

const HamburgerIcon = ({ open }) => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    {open ? <><line x1="6" y1="6" x2="18" y2="18"/><line x1="18" y1="6" x2="6" y2="18"/></> :
            <><line x1="3" y1="6" x2="21" y2="6"/><line x1="3" y1="12" x2="21" y2="12"/><line x1="3" y1="18" x2="21" y2="18"/></>}
  </svg>
);

const navLinks = [
  { label: 'Vergleich', href: '/angebot-vergleichen' },
  { label: 'Methode',   href: '/so-funktioniert-es' },
  { label: 'Förderung', href: '/förderung-finder' },
  { label: 'Kontakt',   href: '/kontakt' },
];

const footerNav = [
  { label: 'Vergleich',  href: '/angebot-vergleichen' },
  { label: 'Methode',    href: '/so-funktioniert-es' },
  { label: 'Förderung',  href: '/förderung-finder' },
  { label: 'Kontakt',    href: '/kontakt' },
];

const legalNav = [
  { label: 'Impressum',   href: '/impressum' },
  { label: 'Datenschutz', href: '/datenschutz' },
  { label: 'AGB',         href: '/agb' },
  { label: 'Widerruf',    href: '/widerruf' },
];

export function V14ComparisonNav({ brand = 'Waemo' }) {
  const isMobile = useIsMobile();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  return (
    <nav style={{
      display: 'flex', alignItems: 'center', justifyContent: 'space-between',
      padding: isMobile ? '12px 16px' : '14px 32px',
      borderBottom: `1px solid ${theme.border}`, background: theme.bg,
      fontFamily: theme.font, color: theme.fg, position: 'relative',
    }}>
      <Link href="/" style={{ fontWeight: 700, fontSize: 16, letterSpacing: '-0.01em', color: 'inherit', textDecoration: 'none' }}>{brand}</Link>
      {!isMobile && (
        <>
          <div style={{ display: 'flex', gap: 22, fontSize: 13, color: theme.muted }}>
            {navLinks.map(l => (
              <Link key={l.label} href={l.href} style={{ color: 'inherit', textDecoration: 'none' }}>{l.label}</Link>
            ))}
          </div>
          <Link href="/angebot-vergleichen" style={{
            padding: '8px 14px', fontSize: 13, fontWeight: 600,
            background: theme.accent, color: theme.accentFg,
            border: 'none', borderRadius: 6, cursor: 'pointer',
            fontFamily: theme.font, textDecoration: 'none', display: 'inline-block',
          }}>Angebote vergleichen</Link>
        </>
      )}
      {isMobile && (
        <button
          aria-label="Menü"
          onClick={() => setIsMenuOpen(o => !o)}
          style={{ background: 'transparent', border: 'none', color: theme.fg, cursor: 'pointer', padding: 4, display: 'flex' }}
        >
          <HamburgerIcon open={isMenuOpen} />
        </button>
      )}
      {isMobile && isMenuOpen && (
        <div style={{
          position: 'absolute', top: '100%', left: 0, right: 0,
          background: theme.bg, borderBottom: `1px solid ${theme.border}`,
          padding: '14px 16px 18px', display: 'flex', flexDirection: 'column', gap: 14,
          zIndex: 50,
        }}>
          {navLinks.map(l => (
            <Link key={l.label} href={l.href} style={{ color: theme.fg, textDecoration: 'none', fontSize: 15 }}>{l.label}</Link>
          ))}
          <Link href="/angebot-vergleichen" style={{
            padding: '10px 14px', fontSize: 14, fontWeight: 600,
            background: theme.accent, color: theme.accentFg,
            border: 'none', borderRadius: 6,
            textAlign: 'center', textDecoration: 'none', display: 'block', marginTop: 4,
          }}>Angebote vergleichen</Link>
        </div>
      )}
    </nav>
  );
}

export function V14ComparisonFooter({ brand = 'Waemo' }) {
  const isMobile = useIsMobile();
  return (
    <footer style={{
      padding: isMobile ? '24px 16px' : '40px 56px', background: '#fff', borderTop: `1px solid ${theme.border}`,
    }}>
      <div style={{
        display: 'grid',
        gridTemplateColumns: isMobile ? '1fr' : '1fr 2fr 1fr',
        alignItems: isMobile ? 'flex-start' : 'center',
        gap: isMobile ? 16 : 24, marginBottom: 18,
      }}>
        <Link href="/" style={{ fontSize: 16, fontWeight: 700, letterSpacing: '-0.015em', color: theme.fg, textDecoration: 'none' }}>
          <span style={{ color: theme.accent, marginRight: 6 }}>★</span>
          {brand}
        </Link>
        <nav style={{
          display: 'flex',
          flexDirection: isMobile ? 'column' : 'row',
          justifyContent: isMobile ? 'flex-start' : 'center',
          gap: isMobile ? 10 : 28,
        }}>
          {footerNav.map(n => (
            <Link key={n.label} href={n.href} style={{
              fontFamily: theme.font, fontSize: 14, color: theme.fg,
              textDecoration: 'none', fontWeight: 500,
            }}>{n.label}</Link>
          ))}
        </nav>
        <div style={{
          fontFamily: theme.mono, fontSize: 11, color: theme.muted,
          letterSpacing: '0.14em', textTransform: 'uppercase',
          textAlign: isMobile ? 'left' : 'right',
        }}>Vergleich · Vermittlung · DSGVO</div>
      </div>
      <div style={{
        paddingTop: 18, borderTop: `1px solid ${theme.border}`,
        fontSize: 12, color: theme.muted, textAlign: 'center',
      }}>
        {`© ${new Date().getFullYear()} ${brand}`}
      </div>
      <div style={{
        display: 'flex',
        flexDirection: isMobile ? 'column' : 'row',
        justifyContent: 'center',
        alignItems: isMobile ? 'center' : 'baseline',
        gap: isMobile ? 8 : 16,
        fontSize: 12, color: theme.muted, marginTop: 16,
      }}>
        {legalNav.map(l => (
          <Link key={l.label} href={l.href} style={{ color: 'inherit', textDecoration: 'none' }}>
            {l.label}
          </Link>
        ))}
      </div>
    </footer>
  );
}
